import { queryClient } from '../lib/queryClient';
import { isMemberMode, setMemberMode } from '../lib/viewMode';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type {
  AuthResponse,
  AuthUser,
  CookieConsentChoice,
  LoginResult,
  SetupInput,
  SetupStatus,
} from '@gwc/shared';
import { isOtpRequired } from '@gwc/shared';
import { api, ApiError } from '../lib/api';
import { getDeviceId } from '../lib/deviceId';

interface AuthContextValue {
  adminEligible: boolean;
  memberMode: boolean;
  toggleMemberMode: () => Promise<void>;
  user: AuthUser | null;
  loading: boolean;
  needsSetup: boolean;
  setupStatus: SetupStatus | null;
  setupStatusError: string | null;
  cookieConsentRequired: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyOtp: (challengeId: string, code: string) => Promise<void>;
  setup: (input: SetupInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  saveCookieConsent: (choice: CookieConsentChoice) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function checkCookieConsent(): Promise<boolean> {
  try {
    const deviceId = getDeviceId();
    const res = await api.get<{ cookieConsentRequired: boolean }>(
      `/auth/device-consent?deviceId=${encodeURIComponent(deviceId)}`,
    );
    return res.cookieConsentRequired;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [memberMode, setMode] = useState(isMemberMode());
  const toggleMemberMode = async () => {
    await queryClient.cancelQueries();
    setMemberMode(!memberMode);
    queryClient.removeQueries();
    setMode(!memberMode);
  };
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);
  const [setupStatusError, setSetupStatusError] = useState<string | null>(null);
  const [cookieConsentRequired, setCookieConsentRequired] = useState(false);

  async function restoreSession(): Promise<AuthUser | null> {
    const deviceId = getDeviceId();
    const path = `/auth/session?deviceId=${encodeURIComponent(deviceId)}`;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.get<{ user: AuthUser; cookieConsentRequired?: boolean }>(path);
        setUser(res.user);
        setCookieConsentRequired(res.cookieConsentRequired ?? (await checkCookieConsent()));
        return res.user;
      } catch (err) {
        const retryable =
          err instanceof ApiError &&
          (err.status === 0 || err.status >= 500 || err.code === 'refresh_raced');
        if (retryable && attempt < 2) {
          await sleep(200 * (attempt + 1));
          continue;
        }
        if (!(err instanceof ApiError)) console.error(err);
        setUser(null);
        setCookieConsentRequired(false);
        return null;
      }
    }
    return null;
  }

  async function loadUser(): Promise<AuthUser | null> {
    return restoreSession();
  }

  async function refreshUser(): Promise<void> {
    await loadUser();
  }

  async function loadSetupStatus(): Promise<void> {
    try {
      const res = await api.get<SetupStatus>('/auth/setup-status');
      setSetupStatus(res);
      setNeedsSetup(res.needsSetup);
      setSetupStatusError(null);
    } catch (err) {
      setSetupStatus(null);
      setNeedsSetup(false);
      setSetupStatusError(
        err instanceof ApiError
          ? err.message
          : 'Could not check whether first-run setup is needed. The API may be down.',
      );
    }
  }

  useEffect(() => {
    void (async () => {
      const u = await loadUser();
      if (!u) await loadSetupStatus();
      setLoading(false);
    })();
  }, []);

  function applyAuthResponse(res: AuthResponse) {
    setUser(res.user);
    setNeedsSetup(false);
    setCookieConsentRequired(res.cookieConsentRequired ?? false);
  }

  async function login(email: string, password: string): Promise<LoginResult> {
    const deviceId = getDeviceId();
    const res = await api.post<LoginResult>('/auth/login', {
      email,
      password,
      deviceId,
      client: 'web',
    });
    if (!isOtpRequired(res)) {
      applyAuthResponse(res);
    }
    return res;
  }

  async function verifyOtp(challengeId: string, code: string) {
    const deviceId = getDeviceId();
    const res = await api.post<AuthResponse>('/auth/verify-otp', {
      challengeId,
      code,
      deviceId,
      client: 'web',
    });
    applyAuthResponse(res);
  }

  async function setup(input: SetupInput) {
    const res = await api.post<AuthResponse>('/auth/setup', input);
    setUser(res.user);
    setNeedsSetup(false);
    setCookieConsentRequired(await checkCookieConsent());
  }

  async function logout() {
    await api.post('/auth/logout').catch(() => undefined);
    setUser(null);
    setCookieConsentRequired(false);
    // Member view is a per-admin choice; the next person to sign in on this
    // browser starts from their own real view.
    setMemberMode(false);
    setMode(false);
  }

  async function saveCookieConsent(choice: CookieConsentChoice) {
    const deviceId = getDeviceId();
    await api.put('/auth/device-consent', { deviceId, choice });
    setCookieConsentRequired(false);
  }

  const adminEligible = !!user?.roles.some(
    (r) => r === 'site_admin' || r === 'tasting_admin' || r === 'records_admin',
  );

  // A remembered member view only means something to an admin. If whoever is
  // signed in now isn't one, drop it so the flag can't linger in storage.
  useEffect(() => {
    if (user && !adminEligible && memberMode) {
      setMemberMode(false);
      setMode(false);
    }
  }, [user, adminEligible, memberMode]);

  return (
    <AuthContext.Provider
      value={{
        user: memberMode && adminEligible && user ? { ...user, roles: ['user'] } : user,
        adminEligible,
        memberMode: memberMode && adminEligible,
        toggleMemberMode,
        loading,
        needsSetup,
        setupStatus,
        setupStatusError,
        cookieConsentRequired,
        login,
        verifyOtp,
        setup,
        logout,
        refreshUser,
        saveCookieConsent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
